import { Gift, Info } from "lucide-react";
import { motion } from "framer-motion";
import { calculateBonus, calculateTotalWithBonus, AppConstants } from "@/constants/theme";

interface BonusSummaryCardProps {
  amount: number;
  type: "deposit" | "withdraw";
  brandName?: string;
  accentColor?: string;
  bonusFactor?: number;
}

export function BonusSummaryCard({
  amount,
  type,
  brandName = "EASYSERVICE",
  accentColor = "#002366",
  bonusFactor,
}: BonusSummaryCardProps) {
  const hasBonusEnabled = bonusFactor != null && bonusFactor > 1;
  const bonus = calculateBonus(amount, type, bonusFactor);
  const total = calculateTotalWithBonus(amount, type, bonusFactor);

  if (!hasBonusEnabled) {
    return null;
  }
  const hasBonus = bonus > 0;

  const depositLabel = "Vous déposez";
  const withdrawLabel = "Retrait demandé";
  const totalLabel = "Vous recevez";
  const topLabel = type === "deposit" ? depositLabel : withdrawLabel;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.05 }}
      className="rounded-2xl p-5 border bg-card ring-1 ring-border"
      style={{ borderColor: `${accentColor}33` }}
    >
      <div className="space-y-4">
        {/* Top row: base amount */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground font-medium">{topLabel}</span>
          <span className="text-xl font-bold text-foreground">
            {amount.toLocaleString()} FCFA
          </span>
        </div>

        {/* Divider */}
        <div className="h-px bg-border" />

        {hasBonus ? (
          <>
            {/* Bonus row */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Gift className="w-5 h-5" style={{ color: accentColor }} />
                <span className="text-sm font-medium text-foreground">
                  Bonus {brandName}
                </span>
              </div>
              <span className="text-lg font-bold" style={{ color: accentColor }}>
                +{bonus.toLocaleString()} FCFA
              </span>
            </div>

            {/* Divider */}
            <div className="h-px bg-border" />

            {/* Total row */}
            <div className="flex items-center justify-between">
              <span className="text-base font-semibold text-foreground">{totalLabel}</span>
              <span className="text-2xl font-bold" style={{ color: accentColor }}>
                {total.toLocaleString()} FCFA
              </span>
            </div>
          </>
        ) : (
          <>
            {/* Pas de bonus : message explicatif */}
            <div className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2.5">
              <Info className="w-4 h-4 shrink-0 text-muted-foreground" />
              <p className="text-xs text-muted-foreground leading-relaxed">
                Bonus {brandName} disponible à partir de{" "}
                <span className="font-semibold text-foreground">
                  {AppConstants.bonusMinAmount.toLocaleString()} FCFA
                </span>{" "}
                {type === "deposit" ? "de dépôt" : "de retrait"}.
              </p>
            </div>

            {/* Divider */}
            <div className="h-px bg-border" />

            {/* Total row (égal au montant de base) */}
            <div className="flex items-center justify-between">
              <span className="text-base font-semibold text-foreground">{totalLabel}</span>
              <span className="text-2xl font-bold text-foreground">
                {total.toLocaleString()} FCFA
              </span>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
}

export default BonusSummaryCard;